import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

export const updateOrderItemSkill: Skill = {
  id: "updateOrderItem",
  description:
    "Actualiza un ítem de un pedido en curso (cambiar cantidad o reemplazar producto).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      orderId: { type: "string" },
      itemId: { type: "string" },
      quantity: { type: "number" },
      productId: { type: "string" },
    },
    required: ["restaurantId", "orderId", "itemId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const orderId = String(args.orderId ?? "");
    const itemId = String(args.itemId ?? "");
    if (!restaurantId || !orderId || !itemId) return { ok: false, error: "Faltan IDs." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (Number.isInteger(Number(args.quantity)) && Number(args.quantity) >= 1) {
      patch.quantity = Number(args.quantity);
      details.push({ label: "Nueva cantidad", value: `${patch.quantity}` });
    }
    if (args.productId) {
      patch.productId = String(args.productId);
      details.push({ label: "Reemplazar producto", value: "(otro)" });
    }
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({
      title: "Modificar ítem del pedido",
      details,
      confirmLabel: "Aplicar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Modificando ítem…",
      successMsg: "✓ Ítem actualizado",
      restaurantId,
      targetTab: "corregir-pedido",
      targetTabLabel: "Corregir Pedido",
      run: async () => {
        try {
          await apiWithAuth.patch(
            `/restaurants/${restaurantId}/orders/${orderId}/items/${itemId}`,
            patch,
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "orders", action: "updated", restaurantId, id: orderId });
          return {
            ok: true as const,
            result: { orderId, itemId, ...patch },
            humanSummary: "Ítem actualizado.",
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const removeOrderItemSkill: Skill = {
  id: "removeOrderItem",
  description: "Elimina un ítem de un pedido en curso.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      orderId: { type: "string" },
      itemId: { type: "string" },
    },
    required: ["restaurantId", "orderId", "itemId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const orderId = String(args.orderId ?? "");
    const itemId = String(args.itemId ?? "");
    if (!restaurantId || !orderId || !itemId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Eliminar ítem del pedido",
      description: "El ítem será removido del pedido en curso.",
      confirmLabel: "Eliminar",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Quitando ítem…",
      successMsg: "✓ Ítem eliminado",
      restaurantId,
      targetTab: "corregir-pedido",
      targetTabLabel: "Corregir Pedido",
      run: async () => {
        try {
          await apiWithAuth.delete(
            `/restaurants/${restaurantId}/orders/${orderId}/items/${itemId}`,
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "orders", action: "updated", restaurantId, id: orderId });
          return {
            ok: true as const,
            result: { orderId, itemId, removed: true },
            humanSummary: "Ítem eliminado del pedido.",
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
